import { action, autorun, computed, makeObservable, observable, reaction } from 'mobx';
import { gameConfigStore, userInfoStore } from '@pascal/pascal-core/shared/core/store';
import { freeBetStore, notificationStore, parseNumberByPrecision } from '@pascal/pascal-core';
import { CHIP_COLORS } from '../enums/chipColor';
import { soundService, soundTypes } from '../services/SoundService';
import { winningAmountStore } from './winningAmountStore';
import { gameStore } from './gameStore';
import { betResponseCodeByName } from '../enums/betResponseCode';
import { BET_TYPES_NAMES, MARKET_TYPES } from '../enums/betTypes';
import { mathConfig, mathJS } from '../enums/mathFormat';

class DoBetStore {
  selectedChip = 10;

  totalBet = 0;

  reBet = null;

  reBetAmount = 0;

  betsModels = [];

  quickBets = [];

  isFirstBetInRound = false;

  constructor() {
    makeObservable(this, {
      totalBet: observable,
      quickBets: observable,
      reBetAmount: observable,
      selectedChip: observable,
      isFirstBetInRound: observable,
      reBet: observable.deep,
      betsModels: observable.deep,
      setReBet: action.bound,
      setDouble: action.bound,
      setUndoBet: action.bound,
      setTotalBet: action.bound,
      setBetsModel: action.bound,
      setQuickBets: action.bound,
      setReBetAmount: action.bound,
      resetBetsModels: action.bound,
      setSelectedChip: action.bound,
      updateBetsModel: action.bound,
      setIsFirstBetInRound: action.bound,
      resetDoBetStore: action.bound,
      lastBetModel: computed,
      limitsByBetType: computed,
      betsTotalAmount: computed,
    });

    autorun(() => {
      if (freeBetStore.activeFreeBet.id) {
        this.setSelectedChip(freeBetStore.activeFreeBet.amount);
      }
    });

    reaction(
      () => gameStore.isBetsClosed,
      (value) => {
        freeBetStore.setIsAllowChangeFreeBet(!value);
      }
    );

    reaction(
      () => freeBetStore.activeFreeBet.id,
      (value, previousValue) => {
        if (previousValue && !value) {
          const { limits } = gameConfigStore;
          this.resetBetsModels();
          this.setReBet(null);
          this.setSelectedChip(limits?.quickBets?.[0]);
        }
        if (previousValue !== value) {
          this.resetBetsModels();
          this.setReBet(null);
        }
      }
    );
  }

  setSelectedChip(value) {
    this.selectedChip = value;
  }

  setUndoBet() {
    winningAmountStore.setLastWin(null);
    if (this.betsModels.length) {
      soundService.play(soundTypes.chip);
    }
    this.betsModels.pop();
  }

  setBetsModel(betsModel) {
    this.betsModels = [betsModel];
  }

  setIsFirstBetInRound(bool) {
    this.isFirstBetInRound = bool;
  }

  setDouble() {
    const { balance } = userInfoStore;
    if (this.betsModels.length) {
      const isDoubleValidInMaxBet = this.lastBetModel.betModel.some(
        (item) => item.amount * 2 > this.limitsByBetType[BET_TYPES_NAMES[item.type]].maxBet
      );
      const isDoubleValidInBalance = this.betsTotalAmount * 2 > balance;

      if (!isDoubleValidInMaxBet && !isDoubleValidInBalance) {
        const doubleBetModel = this.betsModels[this.betsModels.length - 1].betModel.map((item) => ({
          ...item,
          amount: +mathJS.format(item.amount * 2, mathConfig),
        }));
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: doubleBetModel,
          },
        ];
        soundService.play(soundTypes.chip);
        return;
      }
      if (isDoubleValidInMaxBet) {
        this.handleNotificationCode(betResponseCodeByName.InValidMaxBet);
        return;
      }
      this.handleNotificationCode(betResponseCodeByName.NotEnoughBalance);
    }
  }

  get lastBetModel() {
    return this.betsModels[this.betsModels.length - 1] || {};
  }

  setReBet(betData) {
    this.reBet = betData;
  }

  setReBetAmount(amount) {
    this.reBetAmount = amount;
  }

  resetBetsModels() {
    this.betsModels = [];
  }

  get limitsByBetType() {
    const { limits } = gameConfigStore;
    const { activeFreeBet } = freeBetStore;
    const { sideBetsLimits, maxBet, minBet } = limits;
    if (activeFreeBet.id) {
      const freeChipLimits = {
        maxBet: activeFreeBet.amount > maxBet ? maxBet : activeFreeBet.amount,
        minBet: activeFreeBet.amount < minBet ? minBet : activeFreeBet.amount,
      };

      return {
        [BET_TYPES_NAMES[0]]: freeChipLimits,
        [BET_TYPES_NAMES[1]]: freeChipLimits,
        [BET_TYPES_NAMES[2]]: freeChipLimits,
      };
    }

    return {
      [BET_TYPES_NAMES[0]]: limits,
      [BET_TYPES_NAMES[1]]: sideBetsLimits.sidePerfectPair,
      [BET_TYPES_NAMES[2]]: sideBetsLimits.side21plus3,
    };
  }

  calculateSummary(array) {
    return array.reduce((acc, current) => acc + current);
  }

  get betsTotalAmount() {
    if (this.lastBetModel.betModel?.length) {
      return this.lastBetModel.betModel.reduce((acc, current) => {
        const amount = current?.amount || 0;
        acc = +mathJS.format(amount + acc, mathConfig);
        return parseNumberByPrecision(1, acc, +userInfoStore.precision || 2);
      }, 0);
    }
    return 0;
  }

  updateBetsModel(data, callBack) {
    if (gameStore.isBetsClosed) {
      return;
    }
    const { balance } = userInfoStore;
    const { activeFreeBet } = freeBetStore;

    if (
      !this.lastBetModel?.betModel?.some((item) => item.type === MARKET_TYPES.main) &&
      data.type !== MARKET_TYPES.main
    ) {
      this.handleNotificationCode(betResponseCodeByName.DoChipWarning);
      return;
    }

    if (this.betsTotalAmount + this.selectedChip > balance && !activeFreeBet.id) {
      this.handleNotificationCode(betResponseCodeByName.NotEnoughBalance);
      return;
    }
    if (this.selectedChip > this.limitsByBetType[BET_TYPES_NAMES[data.type]].maxBet) {
      this.handleNotificationCode(betResponseCodeByName.InValidMaxBet);
      return;
    }
    if (!this.betsModels.length) {
      this.betsModels.push({
        betModel: [data],
      });
    } else {
      const betTypeTotalAmount = this.lastBetModel.betModel.find((item) => item.type === data.type);
      const otherBetModel = this.lastBetModel.betModel.filter((item) => item.type !== data.type);

      if (betTypeTotalAmount) {
        let amount = parseNumberByPrecision(
          1,
          +mathJS.format(betTypeTotalAmount.amount + data.amount, mathConfig),
          +userInfoStore.precision
        );
        if (betTypeTotalAmount.amount === this.limitsByBetType[BET_TYPES_NAMES[data.type]].maxBet) {
          this.handleNotificationCode(betResponseCodeByName.InValidMaxBet);
          return;
        }

        if (amount > this.limitsByBetType[BET_TYPES_NAMES[data.type]].maxBet) {
          amount = this.limitsByBetType[BET_TYPES_NAMES[data.type]].maxBet;
        }
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: [...otherBetModel, { ...betTypeTotalAmount, amount }],
          },
        ];
      } else {
        this.betsModels = [
          ...this.betsModels,
          {
            betModel: [...otherBetModel, { ...data }],
          },
        ];
      }
    }
    callBack && callBack();
    soundService.play(soundTypes.chip);
  }

  clearDefaultBets() {
    this.betsModels = [];
    this.isDefaultAmount = false;
  }

  setQuickBets(quickBets) {
    this.quickBets = CHIP_COLORS.map((color, index) => ({
      color,
      id: color,
      chip: quickBets[index],
    }));
  }

  setTotalBet(totalBetAmount) {
    this.totalBet = totalBetAmount;
  }

  handleNotificationCode(notification) {
    notificationStore.setNotificationType(notification);
    setTimeout(() => notificationStore.setNotificationType(null), 1000);
  }

  resetDoBetStore() {
    this.selectedChip = 10;
    this.totalBet = 0;
    this.reBet = null;
    this.reBetAmount = 0;
    this.betsModels = [];
    this.quickBets = [];
    this.isFirstBetInRound = false;
  }
}

export const doBetStore = new DoBetStore();
